import { ThemePreference} from "@/types/Theme";
import { AccountLevel, ApiError, BrandMap, HandoffOptions } from "@devour/client";
import {overwolfGameInfo} from "@/hooks/useOverwolfInterop";

import { OverwolfUserInfo } from "@/types/OverwolfUserInfo";

/*
 * HandoffOptions is re-exported from @devour/client rather than re-declared.
 * The local copy (added 2025-11-29 "for Docker build compatibility") drifted
 * into a second, incompatible type once the generator started emitting enums
 * as `const ... as const` unions instead of `export enum` — every call site
 * passing a client HandoffOptions into a store-typed slot stopped compiling.
 * 22 files already import it straight from @devour/client and always did, so
 * the ESM/CJS concern that motivated the copy is not load-bearing.
 */
export { HandoffOptions };

export enum ErrorType {
    APP = "APP",
    API = "API",
    FORM = "FORM",
}

// Game notification state interfaces
export interface GameNotificationState {
    gameDetectedVisible: boolean;
    gameExitVisible: boolean;
    currentGameInfo: overwolfGameInfo | null;
    lastGameExitInfo?: {
        gameInfo: overwolfGameInfo;
        sessionDuration?: number;
        xpEarned?: number;
        questsCompleted?: number;
        exitedAt: number;
    };
}

export interface GameNotificationPreferences {
    showGameDetectedNotifications: boolean;
    showGameExitNotifications: boolean;
    autoEnterGameMode: boolean;
    dismissedGames: string[]; // Game IDs that user chose "don't show again"
}

export interface IMetaStore {
    loadingIncrement?: number;
    errors?: Array<ApiError>;
    menuOrderError: { merchantId?: string; errorMessage?: string; };
    devourIqMenuOrderError: { merchantId?: string; errorMessage?: string; };
    sidebarOpen: boolean;
    globalContactModalVisible: boolean;
    activeModals: number; // Helps manage nested modal render order.
    themePreference: ThemePreference;
    menuOrders: { [key: string]: string };
    menuOrdersRefresh: number;
    lastSearchedPlaceId: string;
    lastMenuOrderId: string;
    lastMerchantId: string;
    lastBrandMap: BrandMap;
    showCartDrawer: boolean;
    accountLevel: AccountLevel;
    handoff: HandoffOptions;
    lastDeletionEmailSent?: number;
    sidebarModalActive: boolean; // handle modals toggled by sidebar to control sticky settings on categories
    activeGameModal: string; // handle active game modal
    fuelPriceExpiryTime: number;
    forceRefreshTransactions: boolean; // handle FUEL slippage errors, forcing refresh
    isIndustryDismissed: boolean; // if toggled then the industry promo modal won't show again
    isIndustryDismissedForSession: boolean; // same as above, but handles a session long dismissal
    isBottomNavHidden: boolean; // toggle bottom nav on tablet/mobile view
    isGoFrensTutorialBannerDismissed: boolean; // if toggled, the 'How does GoFriends work?' won't show again
    isShowStickyFooter: boolean; // toggles the sticky footer nav on mobile and tablet, toggling is used in the merchants pages
    isChatOpen: boolean;
    isLoginOpen: boolean;
    isCheckoutFuelBannerDismissed?: boolean;
    isOnOverwolf: boolean;
    overwolfGameInfo: overwolfGameInfo | null;
    isMenuOrderUpdating: boolean;
    isFuelUpModalDismissed: boolean;
    overwolfUserInfo: OverwolfUserInfo | null;
    isOverwolfDiscordBannerDismissed: boolean;
    isDevourplayFeatureModalDismissed: boolean;
    streamerModeEnabled: boolean;
    dismissedBrandChallengeOnboardings: Array<string>;
    thirdwebReauthenticateModal: boolean;
    /*
     * Request to open the thirdweb wallet-details modal — set by the nav "Devour
     * Wallet" action; consumed by the lazy WalletRuntime so opening a wallet is
     * what mounts thirdweb, not the nav rendering.
     */
    walletDetailsModal: boolean;
    showInviteModal: boolean;

    // Game notification system
    gameNotifications: GameNotificationState;
    gameNotificationPreferences: GameNotificationPreferences;
}

export default {
    loadingIncrement: 0,
    errors: [],
    menuOrderError: {},
    devourIqMenuOrderError: {},
    sidebarOpen: false,
    globalContactModalVisible: false,
    activeModals: 0,
    themePreference: ThemePreference.DARK,
    menuOrders: undefined,
    menuOrdersRefresh: 0,
    lastMenuOrderId: undefined,
    lastMerchantId: undefined,
    lastBrandMap: undefined,
    lastSearchedPlaceId: undefined,
    showCartDrawer: false,
    accountLevel: null,
    handoff: HandoffOptions.DELIVERY,
    sidebarModalActive: false,
    activeGameModal: "",
    fuelPriceExpiryTime: undefined,
    forceRefreshTransactions: false,
    isIndustryDismissed: false,
    isIndustryDismissedForSession: false,
    isBottomNavHidden: false,
    isGoFrensTutorialBannerDismissed: false,
    isShowStickyFooter: false,
    isChatOpen: false,
    isLoginOpen: false,
    isOnOverwolf: false,
    overwolfGameInfo: null,
    isCheckoutFuelBannerDismissed: false,
    isMenuOrderUpdating: false,
    overwolfUserInfo: null,
    isOverwolfDiscordBannerDismissed: false,
    isDevourplayFeatureModalDismissed: false,
    streamerModeEnabled: false,
    isFuelUpModalDismissed: false,
    dismissedBrandChallengeOnboardings: undefined,
    thirdwebReauthenticateModal: false,
    walletDetailsModal: false,
    showInviteModal: false,

    // Game notification system defaults
    gameNotifications: {
        gameDetectedVisible: false,
        gameExitVisible: false,
        currentGameInfo: null,
        lastGameExitInfo: undefined,
    },
    gameNotificationPreferences: {
        showGameDetectedNotifications: true,
        showGameExitNotifications: true,
        autoEnterGameMode: false,
        dismissedGames: [],
    },
} as IMetaStore;
